import { useMutation, useQuery, useQueryClient } from 'react-query';
import { API_URL, queryDelete, queryGet, queryUpdate } from './fetch';
import { Calendar } from './calendar';
import { useCallback } from 'react';

export enum IntegrationCapability {
  CALENDAR = 0b01,
  VISIO = 0b10,
}

export enum ProviderEnum {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  CRONOFY = 'cronofy',
}

export interface IntegrationAccount {
  id: string;
  accountId?: string;
  provider?: ProviderEnum;
  name?: string;
  capabilities: number;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: Date;
  lastUpdate?: Date;
  connected: boolean;
  scope?: string;
  deletedAt: Date;

  calendars: Calendar[];
}

export const useIntegrations = () => {
  return useQuery<IntegrationAccount[]>(
    ['integrations'],
    queryGet(`/me/integrations`),
  );
};

export const useUpdateCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation<Calendar, undefined, { id: string; isVisible?: boolean }>(
    (data) => queryUpdate(`/v2/calendars/${data.id}`)(data),
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(['integrations']);
      },
    },
  );
};

export const useReconnectToAccount = (accountId?: string) => {
  return useCallback(() => {
    // We use "window.open" to be able to close the window after the user has authorized the calendar
    // "window.close" can only be called on windows that were opened by a script
    window.open(`${API_URL}/integrations/${accountId}/reconnect`, '_blank');
  }, [accountId]);
};

export const useDeleteIntegration = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>(queryDelete(`/integrations/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      queryClient.invalidateQueries(['me', 'conferencings']);
    },
  });
};

export const useDeleteAllIntegrations = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>(queryDelete(`/me/integrations`), {
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      queryClient.invalidateQueries(['me', 'conferencings']);
    },
  });
};
